import { useEffect, useRef, useState } from 'react'
import CanvasJSReact from '@canvasjs/react-charts'
import { CHART_DEFAULT_OPTIONS, SUBTITLE_FONT_SIZE, TITLE_FONT_SIZE } from '../../common/constants'
import { convertTimestamp } from '../../common/helpers'
import { formatDateForChart, roundToDecimal } from '../../common/utils'
import { ChartSubHeader } from '../ChartSubHeader'
import { CollapsibleSection } from '../CollapsibleSection'
import { SensorProduct, LINE_COLORS, DeviceInfo } from '../../common/enums'
import { convertBaseToDisplay } from '../../common/conversions'
import { Viewport } from '../../common/types'

const { CanvasJSChart } = CanvasJSReact

const PRESSURE_UPPER_LIMIT_PASCALS = 200000

interface Props {
  device: DeviceInfo
  sensorType: SensorProduct
  sensorReadings: Record<string, any>
  viewport: Viewport
  userUnitSettings: any
  isLoading: boolean
  zoomEnabled?: boolean
}

export function UnifiedChartIrrometer({
  device,
  sensorType,
  sensorReadings,
  viewport,
  userUnitSettings,
  isLoading,
  zoomEnabled = true,
}: Props) {
  const chartRef = useRef()
  // const canvasRef = useRef()

  const [visible, setVisible] = useState<Record<string, boolean>>({})

  let trackDay = -1

  useEffect(() => {
    // // const chartContainer = chartRef.current.container
    // const canvas = chartContainer.querySelector('canvas')
    // if (canvas) {
    //   canvasRef.current = canvas
    //   const ctx = canvas.getContext('2d', { willReadFrequently: true })
    //   // Perform any additional operations with the context here, if necessary
    //   console.log('Canvas context with willReadFrequently:', ctx)
    // }
  }, [chartRef])

  useEffect(() => {
    const visible: Record<string, boolean> = {}

    Object.keys(sensorReadings)
      .filter((key: string) => !key.includes('T0'))
      .map((key: string) => {
        const sensorName = sensorReadings[key].metadata.sensorName
        visible[sensorName] = true
        return null
      })

    setVisible(visible)
  }, [sensorReadings])

  const minimum = 0
  let maximum = 0

  const dataPoints: Record<string, any> = {}

  const { formulas, abbreviations } = Object.values(sensorReadings)[0]?.metadata.units.conversions
  const unitId = userUnitSettings[Object.values(sensorReadings)[0]?.metadata.units.category.id]
  let displayUnits = abbreviations[unitId]

  Object.keys(sensorReadings)
    .filter((key: string) => !key.includes('T0'))
    .forEach((key: string) => {
      const sensor = sensorReadings[key]

      const sensorName = sensor.metadata.sensorName
      const unitCategoryId = sensor.metadata.units.category.id

      const { unitId, precision } = userUnitSettings[unitCategoryId]
      const conversionFormula = formulas[unitId]
      displayUnits = abbreviations[unitId]

      const data = Object.keys(sensor.readings)
        .filter((key: string) => {
          const timestamp = Number(key) * 1000
          return timestamp >= viewport.minimum && timestamp <= viewport.maximum
        })
        .filter((key: string) => {
          return sensor.readings[key] < PRESSURE_UPPER_LIMIT_PASCALS
        })
        .map((key: string) => {
          const timestamp = Number(key)

          // TODO: handle conversions (pass user conversions)
          const reading = Number(convertBaseToDisplay(sensor.readings[key], conversionFormula).toFixed(precision))

          if (reading > maximum) {
            maximum = reading
          }

          return {
            label: convertTimestamp(timestamp),
            x: timestamp * 1000,
            y: reading,
          }
        })

      dataPoints[sensorName] = data
    })

  const lines =
    Object.keys(dataPoints).length > 0
      ? Object.keys(dataPoints).map((sensorPort: string, index: number) => {
          const sensorName = sensorReadings[index]?.metadata.sensorName
          const points = dataPoints[sensorName]
          const depth = sensorReadings[index]?.metadata.sensorConfig.offset

          return {
            name: `${sensorPort} (${depth}")`,
            type: 'line',
            showInLegend: true,
            xValueType: 'dateTime',
            color: LINE_COLORS[sensorPort],
            visible: visible[sensorName],
            dataPoints: points,
          }
        })
      : []

  const options = {
    ...CHART_DEFAULT_OPTIONS,
    zoomEnabled,
    exportFileName: `${device.deviceName}_SoilMoisture`,
    title: {
      text: 'Soil Moisture',
      fontSize: TITLE_FONT_SIZE,
    },
    subtitles: [
      {
        fontSize: SUBTITLE_FONT_SIZE,
        text: `${device.deviceName} (Site: ${device.siteName?.trim()})`,
      },
    ],
    toolTip: {
      shared: true,
      contentFormatter: (e: any) => {
        // const date = new Date(new Date(e.entries[0].dataPoint.x).setSeconds(0))
        const date = new Date(e.entries[0].dataPoint.x)
        var content = `<span style="color:${e.color}">${date.toLocaleString()}</span><br/>`
        for (var i = 0; i < e.entries.length; i++) {
          content += `<span style="color:${e.entries[i].dataSeries.color}">${
            e.entries[i].dataSeries.name
          }:</span> <strong> ${roundToDecimal(e.entries[i].dataPoint.y)} ${displayUnits} </strong><br/>`
        }
        return content
      },
    },
    axisX: {
      labelFormatter: function (e: any) {
        const date = new Date(e.value)
        const day = date.getDay()
        const dayChanged = trackDay !== day
        trackDay = day

        return formatDateForChart(date, dayChanged)
      },
      viewportMinimum: viewport?.minimum || null,
      viewportMaximum: viewport?.maximum || null,
    },
    axisY: {
      includeZero: false,
      minimum,
      maximum: maximum * 1.2,
      title: `Irrometer (${displayUnits})`,
      titleFontSize: TITLE_FONT_SIZE,
      labelFontSize: TITLE_FONT_SIZE,
      reversed: true,
    },
    data: [...lines],
    legend: {
      itemclick: (e: any) => {
        setVisible((prev) => ({ ...prev, [e.dataSeries.name]: !prev[e.dataSeries.name] }))
      },
    },
  }

  const data = Object.keys(dataPoints).map((sensorPort: string) => {
    const readings = dataPoints[sensorPort]
    const lastIndex = Object.keys(readings).length - 1
    const lastTimestamp = readings[lastIndex] ? new Date(Number(readings[lastIndex]?.x)).toLocaleString() : '...'
    const lastReading = readings[lastIndex] ? Number(readings[lastIndex]?.y).toFixed(1) : ''
    const metadata = sensorReadings.filter((sensor: any) => sensor.metadata.sensorName === sensorPort)[0].metadata

    return [
      `Port ${sensorPort}: ${sensorType}`,
      `${lastTimestamp}`,
      `Depth: ${metadata.sensorConfig.offset}"`,
      `${lastReading} ${displayUnits}`,
    ]
  })

  return (
    <CollapsibleSection title="Soil Moisture" isLoading={isLoading}>
      <ChartSubHeader data={data} />
      <CanvasJSChart options={options} />
    </CollapsibleSection>
  )
}
